import React from 'react';
import { User, Target, BookOpen, Clock } from 'lucide-react';
import type { TeamMember } from '../../types/collaboration';

interface TeamMemberCardProps {
  member: TeamMember;
  rank?: number;
}

export const TeamMemberCard: React.FC<TeamMemberCardProps> = ({ member, rank }) => {
  const getRoleBadge = () => {
    const colors = {
      leader: 'bg-primary-100 text-primary-700',
      member: 'bg-sage-100 text-sage-700',
      contributor: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[member.role]}`}>
        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
      </span>
    );
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${Math.floor(diffDays / 7)}w ago`;
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-sage-400 flex items-center justify-center flex-shrink-0">
          <User className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {rank && (
              <span className="text-lg font-bold text-charcoal-400">#{rank}</span>
            )}
            <h3 className="text-base font-bold text-charcoal-900 truncate">{member.name}</h3>
          </div>
          <p className="text-sm text-charcoal-600 truncate">{member.email}</p>
        </div>
      </div>

      {/* Role Badge */}
      <div className="mb-4">
        {getRoleBadge()}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary-600" />
          <div>
            <p className="text-xs text-charcoal-600">Frameworks</p>
            <p className="text-sm font-semibold text-charcoal-900">{member.frameworksExplored}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-sage-600" />
          <div>
            <p className="text-xs text-charcoal-600">Outcomes</p>
            <p className="text-sm font-semibold text-charcoal-900">{member.outcomesContributed}</p>
          </div>
        </div>
      </div>

      {/* Last Active */}
      <div className="flex items-center gap-1 text-xs text-charcoal-500 pt-3 border-t border-[var(--rule)]">
        <Clock className="h-3 w-3" />
        <span>Active {getTimeAgo(member.lastActive)}</span>
      </div>
    </div>
  );
};

